import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import MattersTable from '../MattersTable.jsx';
import DemandColumnsPicker, { useDemandListColumns } from '../DemandColumnsPicker.jsx';
import { useClientData } from '../ClientLayout.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { PIPELINE_STEPS, getPipelineIndex, boardColumnIdForStatus } from '../../lib/matterPipeline.js';
import { filterMattersByQuery } from '../../lib/matterSearch.js';
import { getLocalSettings } from '../../lib/settingsStore.js';
import {
  archiveMatters,
  exportMattersCsv,
  selectedMattersFromIds,
  syncMattersToAccounting,
  permanentlyDeleteMatters,
  unarchiveMatters,
} from '../../lib/bulkDemandActions.js';
import {
  KANBAN_CARD_FIELDS,
  defaultKanbanFieldIds,
  kanbanCardLines,
  loadKanbanFieldIds,
  saveKanbanFieldIds,
} from '../../lib/kanbanCardFields.js';
import {
  loadCollapsedColumnIds,
  toggleCollapsedColumnId,
} from '../../lib/kanbanColumnCollapse.js';
import { useEffect, useMemo, useRef, useState } from 'react';

const BOARD_COLUMNS = PIPELINE_STEPS.map((step) => ({
  id: step.id,
  title: step.shortLabel,
  detail: step.label,
  tooltip: step.tooltip,
}));

const KANBAN_BULK_TIPS = {
  export: 'Download the selected demands as a CSV spreadsheet.',
  syncConnected:
    'Sync selected demands that have invoice IDs to your connected accounting app and mark those invoices as paid.',
  syncDisconnected:
    'Connect an accounting app in Settings to sync selected demands and mark invoices as paid.',
  archive: 'Move selected demands into Archive. View and restore them anytime from Archived.',
};

function mattersForColumn(matters, columnId) {
  return matters.filter((m) => boardColumnIdForStatus(m.Status) === columnId);
}

function StageInfoTip({ text, label }) {
  return (
    <span className="kanban-stage-tip">
      <button
        type="button"
        className="kanban-stage-tip-btn"
        aria-label={`About ${label}: ${text}`}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span className="kanban-stage-tip-bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function BulkActionTip({ label, text }) {
  return (
    <span className="list-bulk-tip kanban-stage-tip">
      <button
        type="button"
        className="kanban-stage-tip-btn"
        tabIndex={0}
        aria-label={`${label}: ${text}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span className="kanban-stage-tip-bubble list-bulk-tip-bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function KanbanCard({ matter, fieldIds, onOpen, selected, onToggleSelect }) {
  const { title, details } = useMemo(
    () => kanbanCardLines(matter, fieldIds),
    [matter, fieldIds]
  );
  const id = String(matter.ROWID);

  return (
    <div className={`kanban-card${selected ? ' is-selected' : ''}`}>
      <label className="kanban-card-check">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggleSelect(id, e.target.checked)}
          aria-label={`Select ${title}`}
        />
      </label>
      <button type="button" className="kanban-card-body" onClick={() => onOpen(matter)}>
        <strong>{title}</strong>
        {details.map((line) => (
          <span key={line.id} className="kanban-card-line" title={line.label}>
            {line.value}
          </span>
        ))}
      </button>
    </div>
  );
}

export function DemandsKanbanPage() {
  const {
    matters,
    setMatters,
    notice,
    setNotice,
    openMatter,
    accountingConnected,
    archivedMatters: archivedList,
  } = useClientData();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('stage');
  const [fieldIds, setFieldIds] = useState(() => loadKanbanFieldIds());
  const [collapsedIds, setCollapsedIds] = useState(() => loadCollapsedColumnIds());
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const menuRef = useRef(null);
  const bulkMenuRef = useRef(null);

  const boardColumnsStyle = useMemo(() => {
    const tracks = BOARD_COLUMNS.map((col) =>
      collapsedIds.includes(col.id) ? '3.95rem' : 'minmax(180px, 1fr)'
    );
    return { gridTemplateColumns: tracks.join(' ') };
  }, [collapsedIds]);

  const selectedRows = useMemo(
    () => selectedMattersFromIds(matters, selected),
    [matters, selected]
  );

  const matterIds = useMemo(
    () => new Set(matters.map((m) => String(m.ROWID))),
    [matters]
  );

  useEffect(() => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      const next = new Set([...prev].filter((id) => matterIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [matterIds]);

  function toggleColumnCollapsed(columnId) {
    setCollapsedIds((prev) => toggleCollapsedColumnId(prev, columnId));
  }

  useEffect(() => {
    if (!selected.size) setBulkOpen(false);
  }, [selected.size]);

  useEffect(() => {
    if (!fieldsOpen && !bulkOpen) return undefined;
    function onDocClick(e) {
      if (fieldsOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setFieldsOpen(false);
      }
      if (bulkOpen && bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setFieldsOpen(false);
        setBulkOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [fieldsOpen, bulkOpen]);

  function toggleField(id) {
    const field = KANBAN_CARD_FIELDS.find((item) => item.id === id);
    if (!field || field.required) return;
    setFieldIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      return saveKanbanFieldIds(next);
    });
  }

  function resetFields() {
    setFieldIds(saveKanbanFieldIds(defaultKanbanFieldIds()));
  }

  function toggleSelect(id, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleColumnSelect(cardIds, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of cardIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  function mergeUpdated(updatedRows) {
    const byId = new Map(updatedRows.map((m) => [String(m.ROWID), m]));
    setMatters((prev) => prev.map((m) => byId.get(String(m.ROWID)) || m));
  }

  function runBulkAction(action) {
    const rowsSelected = selectedRows;
    setBulkOpen(false);
    action?.(rowsSelected);
    if (action !== handleBulkExport) {
      setSelected(new Set());
    }
  }

  function handleBulkExport(rows) {
    const count = exportMattersCsv(rows);
    setNotice(`Exported ${count} demand${count === 1 ? '' : 's'} to CSV.`);
  }

  function handleBulkSyncAccounting(rows) {
    const result = syncMattersToAccounting(rows);
    mergeUpdated(result.synced);
    const synced = result.synced.length;
    const skipped = result.skipped.length;
    if (!synced && skipped) {
      setNotice('No selected demands had an invoice ID to sync.');
      return;
    }
    setNotice(
      `Synced ${synced} demand${synced === 1 ? '' : 's'} to accounting` +
        (skipped ? ` · skipped ${skipped} without invoice ID` : '') +
        '.'
    );
  }

  function handleBulkArchive(rows) {
    const billing = getLocalSettings(user?.email || '').billing || {};
    const result = archiveMatters(rows, {
      billing,
      currentArchivedCount: archivedList.length,
    });
    if (result.reason === 'LIMIT') {
      setNotice('Unable to archive selected demands. Try again or contact support.');
      return;
    }
    mergeUpdated(result.updated);
    setNotice(
      `Archived ${result.updated.length} demand${result.updated.length === 1 ? '' : 's'}. ` +
        `View them under Archived.`
    );
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Demands</h1>
          <p>
            Kanban view of demands by status. Select cards for bulk export, accounting sync, or
            archive. Filter by column, or collapse columns from the column header. Only your
            attorney can change status.
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/demands">
            List View
          </Link>
          <Link className="btn-ghost" to="/client/demands/archived">
            Archived
            {archivedList.length ? ` (${archivedList.length})` : ''}
          </Link>
          <div className="list-toolbar-menu" ref={menuRef}>
            <button
              type="button"
              className="btn-ghost list-columns-btn"
              aria-expanded={fieldsOpen}
              aria-haspopup="dialog"
              onClick={() => setFieldsOpen((open) => !open)}
            >
              Filter
            </button>
            {fieldsOpen ? (
              <div className="list-columns-menu" role="dialog" aria-label="Filter by column">
                <div className="list-columns-menu-head">
                  <strong>Filter by column</strong>
                  <button type="button" className="text-link strong" onClick={resetFields}>
                    Reset
                  </button>
                </div>
                <ul className="list-columns-options">
                  {KANBAN_CARD_FIELDS.map((field) => {
                    const checked = fieldIds.includes(field.id);
                    return (
                      <li key={field.id}>
                        <label className="list-columns-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={Boolean(field.required)}
                            onChange={() => toggleField(field.id)}
                          />
                          <span>
                            {field.label}
                            {field.required ? ' (required)' : ''}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}

      <div className="list-view-toolbar kanban-bulk-toolbar">
        <p className="list-view-count">
          {matters.length} demand{matters.length === 1 ? '' : 's'}
          {selected.size ? ` · ${selected.size} selected` : ''}
        </p>
        {selected.size ? (
          <div className="list-bulk-menu" ref={bulkMenuRef}>
            <button
              type="button"
              className="btn-ghost list-bulk-toggle"
              aria-expanded={bulkOpen}
              aria-haspopup="menu"
              onClick={() => setBulkOpen((open) => !open)}
            >
              Bulk Actions
              <span aria-hidden>▾</span>
            </button>
            {bulkOpen ? (
              <div className="list-bulk-dropdown" role="menu" aria-label="Bulk demand actions">
                <div className="list-bulk-item-row" role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="list-bulk-item"
                    onClick={() => runBulkAction(handleBulkExport)}
                  >
                    Export
                  </button>
                  <BulkActionTip label="Export" text={KANBAN_BULK_TIPS.export} />
                </div>
                <div className="list-bulk-item-row" role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="list-bulk-item"
                    disabled={!accountingConnected}
                    onClick={() => runBulkAction(handleBulkSyncAccounting)}
                  >
                    Sync to accounting
                  </button>
                  <BulkActionTip
                    label="Sync to accounting"
                    text={
                      accountingConnected
                        ? KANBAN_BULK_TIPS.syncConnected
                        : KANBAN_BULK_TIPS.syncDisconnected
                    }
                  />
                </div>
                <div className="list-bulk-item-row" role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="list-bulk-item is-danger"
                    onClick={() => runBulkAction(handleBulkArchive)}
                  >
                    Archive
                  </button>
                  <BulkActionTip label="Archive" text={KANBAN_BULK_TIPS.archive} />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="kanban-board" role="list" style={boardColumnsStyle}>
        {BOARD_COLUMNS.map((col) => {
          const cards = mattersForColumn(matters, col.id)
            .slice()
            .sort((a, b) => getPipelineIndex(a.Status) - getPipelineIndex(b.Status));
          const cardIds = cards.map((m) => String(m.ROWID));
          const selectedInColumn = cardIds.filter((id) => selected.has(id)).length;
          const allColumnSelected = cardIds.length > 0 && selectedInColumn === cardIds.length;
          const someColumnSelected =
            selectedInColumn > 0 && selectedInColumn < cardIds.length;
          const active = highlight === col.id;
          const collapsed = collapsedIds.includes(col.id);
          const bodyId = `kanban-body-${col.id}`;
          return (
            <section
              key={col.id}
              className={`kanban-column${active ? ' is-highlighted' : ''}${
                collapsed ? ' is-collapsed' : ''
              }`}
              data-stage={col.id}
              aria-label={col.detail}
            >
              <div className="kanban-column-head">
                {!collapsed ? (
                  <label className="kanban-column-check">
                    <input
                      type="checkbox"
                      checked={allColumnSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someColumnSelected;
                      }}
                      disabled={!cardIds.length}
                      onChange={(e) => toggleColumnSelect(cardIds, e.target.checked)}
                      aria-label={`Select all in ${col.title}`}
                    />
                  </label>
                ) : null}
                <button
                  type="button"
                  className="kanban-column-toggle"
                  aria-expanded={!collapsed}
                  aria-controls={bodyId}
                  title={
                    collapsed
                      ? `Expand ${col.title} column`
                      : `Collapse ${col.title} column`
                  }
                  onClick={() => toggleColumnCollapsed(col.id)}
                >
                  <span className="kanban-column-title-row">
                    <strong className="kanban-column-label">{col.title}</strong>
                  </span>
                  <em className="kanban-column-count">{cards.length}</em>
                </button>
                {!collapsed ? (
                  <StageInfoTip text={col.tooltip} label={col.title} />
                ) : null}
              </div>
              {!collapsed ? (
                <div className="kanban-column-body" id={bodyId}>
                  {cards.length ? (
                    cards.map((m) => {
                      const id = String(m.ROWID);
                      return (
                        <KanbanCard
                          key={id}
                          matter={m}
                          fieldIds={fieldIds}
                          onOpen={openMatter}
                          selected={selected.has(id)}
                          onToggleSelect={toggleSelect}
                        />
                      );
                    })
                  ) : (
                    <p className="kanban-empty">No demands</p>
                  )}
                </div>
              ) : (
                <div id={bodyId} hidden />
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function DemandsListPage() {
  const {
    matters,
    setMatters,
    notice,
    setNotice,
    openMatter,
    accountingConnected,
    archivedMatters: archivedList,
  } = useClientData();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const filteredMatters = filterMattersByQuery(matters, query);
  const { visibleIds, toggleColumn, resetColumns } = useDemandListColumns();

  function mergeUpdated(updatedRows) {
    const byId = new Map(updatedRows.map((m) => [String(m.ROWID), m]));
    setMatters((prev) => prev.map((m) => byId.get(String(m.ROWID)) || m));
  }

  function handleBulkExport(rows) {
    const count = exportMattersCsv(rows);
    setNotice(`Exported ${count} demand${count === 1 ? '' : 's'} to CSV.`);
  }

  function handleBulkSyncAccounting(rows) {
    const result = syncMattersToAccounting(rows);
    mergeUpdated(result.synced);
    const synced = result.synced.length;
    const skipped = result.skipped.length;
    if (!synced && skipped) {
      setNotice('No selected demands had an invoice ID to sync.');
      return;
    }
    setNotice(
      `Synced ${synced} demand${synced === 1 ? '' : 's'} to accounting` +
        (skipped ? ` · skipped ${skipped} without invoice ID` : '') +
        '.'
    );
  }

  function handleBulkArchive(rows) {
    const billing = getLocalSettings(user?.email || '').billing || {};
    const result = archiveMatters(rows, {
      billing,
      currentArchivedCount: archivedList.length,
    });
    if (result.reason === 'LIMIT') {
      setNotice('Unable to archive selected demands. Try again or contact support.');
      return;
    }
    mergeUpdated(result.updated);
    setNotice(
      `Archived ${result.updated.length} demand${result.updated.length === 1 ? '' : 's'}. ` +
        `View them under Archived.`
    );
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Demands</h1>
          <p>
            {query.trim()
              ? `Showing demands matching “${query.trim()}”. Customize which columns appear.`
              : 'List View of demands by column. Customize which columns appear by selecting the filter button.'}
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/demands/kanban">
            Kanban View
          </Link>
          <Link className="btn-ghost" to="/client/demands/archived">
            Archived
            {archivedList.length ? ` (${archivedList.length})` : ''}
          </Link>
          <DemandColumnsPicker
            visibleIds={visibleIds}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}
      <MattersTable
        matters={filteredMatters}
        onOpenMatter={openMatter}
        visibleIds={visibleIds}
        accountingConnected={accountingConnected}
        onBulkExport={handleBulkExport}
        onBulkSyncAccounting={handleBulkSyncAccounting}
        onBulkArchive={handleBulkArchive}
      />
    </div>
  );
}

export function DemandsArchivedPage() {
  const {
    archivedMatters: archivedList,
    setMatters,
    notice,
    setNotice,
    openMatter,
  } = useClientData();
  const { visibleIds, toggleColumn, resetColumns } = useDemandListColumns();

  function mergeUpdated(updatedRows) {
    const byId = new Map(updatedRows.map((m) => [String(m.ROWID), m]));
    setMatters((prev) => prev.map((m) => byId.get(String(m.ROWID)) || m));
  }

  function handleBulkUnarchive(rows) {
    const updated = unarchiveMatters(rows);
    mergeUpdated(updated);
    setNotice(
      `Restored ${updated.length} demand${updated.length === 1 ? '' : 's'} to the active list.`
    );
  }

  function handleBulkDelete(rows) {
    const count = rows?.length || 0;
    if (!count) return;
    const ok = window.confirm(
      count === 1
        ? 'Permanently delete this archived demand? This cannot be undone. Files stored in Evidence Locker are kept.'
        : `Permanently delete ${count} archived demands? This cannot be undone. Files stored in Evidence Locker are kept.`
    );
    if (!ok) return;
    const updated = permanentlyDeleteMatters(rows);
    const byId = new Map(updated.map((matter) => [String(matter.ROWID), matter]));
    setMatters((prev) => prev.map((matter) => byId.get(String(matter.ROWID)) || matter));
    for (const matter of updated) {
      const id = encodeURIComponent(matter.ROWID);
      void api.post(`/server/client_api/matters/${id}/delete`).catch(() => {});
    }
    setNotice(
      `Deleted ${updated.length} demand${updated.length === 1 ? '' : 's'} permanently. Evidence Locker files were kept.`
    );
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Archived demands</h1>
          <p>
            Archive is unlimited. Restore demands anytime to work them in the active list. File
            storage is billed separately in Evidence Locker.
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/demands">
            Active list
          </Link>
          <Link className="btn-ghost" to="/client/settings?tab=billing">
            Billing
          </Link>
          <DemandColumnsPicker
            visibleIds={visibleIds}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}
      <MattersTable
        matters={archivedList}
        onOpenMatter={(matter) => openMatter(matter, '/client/demands/archived')}
        visibleIds={visibleIds}
        mode="archived"
        onBulkUnarchive={handleBulkUnarchive}
        onBulkDelete={handleBulkDelete}
      />
    </div>
  );
}
